import React from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import PopoverButton from 'talent_matching/components/filters/popover_button';
import { withLocalePrefix } from 'shared/utils/translation';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import {
  MultiDropdown,
  useMultiDropdown,
} from '@grnhse/seedling/lib/birch/components/forms/dropdown';

const t = withLocalePrefix('talent_matching.filter_row');

// Temp values. todo: after MyGH backend endpoint is ready use appropriate values.
const EMPLOYMENT_TYPE_OPTIONS: Item[] = [
  { label: 'Full time', value: 'full_time' },
  { label: 'Part time', value: 'part_time' },
  { label: 'Contract', value: 'contract' },
  { label: 'Internship', value: 'internship' },
];

const WORKSITE_OPTIONS: Item[] = [
  { label: 'Remote', value: 'remote' },
  { label: 'Hybrid', value: 'hybrid' },
  { label: 'On-site', value: 'on_site' },
];

type MultiSelectFieldProps = {
  label: string;
  ariaLabel: string;
  options: Item[];
  selectedItems: Item[];
  onSelectedItemsChange: (items: Item[]) => void;
};

const MultiSelectField = ({
  label,
  ariaLabel,
  options,
  selectedItems,
  onSelectedItemsChange,
}: MultiSelectFieldProps) => {
  const dropdownProps = useMultiDropdown({
    fetchItems: async () => ({ items: options }),
    selectedItems,
    onSelectedItemsChange: (changes) =>
      onSelectedItemsChange(changes.selectedItems ?? []),
  });

  return (
    <MultiDropdown dropdownProps={dropdownProps}>
      <MultiDropdown.Field
        label={label}
        placeholder={t('advanced_filters.select')}
        ariaLabel={ariaLabel}
        condensedUnitName={t('work_preferences.selections')}
      />
      <MultiDropdown.Flyout disablePortal />
    </MultiDropdown>
  );
};

const WorkPreferencesFiltersGroup = () => {
  const { watch, setValue } = useFormContext();
  const [employmentTypes, worksites, locations, languages, willingToRelocate] =
    watch([
      'workPreferenceEmploymentTypes',
      'workPreferenceWorksites',
      'workPreferenceLocations',
      'workPreferenceLanguages',
      'workPreferenceWillingToRelocate',
    ]);

  const selectedEmploymentTypes = (employmentTypes as Item[]) || [];
  const selectedWorksites = (worksites as Item[]) || [];
  const selectedLocations = (locations as Item[]) || [];
  const selectedLanguages = (languages as Item[]) || [];

  const appliedCount = [
    selectedLanguages.length > 0,
    selectedEmploymentTypes.length > 0,
    selectedWorksites.length > 0,
    selectedLocations.length > 0,
    willingToRelocate,
  ].filter(Boolean).length;

  const buttonLabel = `${t('work_preferences.title')}${
    appliedCount ? ` (${appliedCount})` : ''
  }`;

  const clear = () => {
    setValue('workPreferenceLanguages', [], { shouldDirty: true });
    setValue('workPreferenceEmploymentTypes', [], { shouldDirty: true });
    setValue('workPreferenceWorksites', [], { shouldDirty: true });
    setValue('workPreferenceLocations', [], { shouldDirty: true });
    setValue('workPreferenceWillingToRelocate', false, { shouldDirty: true });
  };

  return (
    <PopoverButton
      buttonLabel={buttonLabel}
      title={t('work_preferences.filters_title')}
      width="500px"
    >
      <Stack spacing={3}>
        <MultiSelectField
          label={t('work_preferences.languages_spoken')}
          ariaLabel={t('work_preferences.languages_spoken')}
          options={[]}
          selectedItems={selectedLanguages}
          onSelectedItemsChange={(items) =>
            setValue('workPreferenceLanguages', items, { shouldDirty: true })
          }
        />

        <MultiSelectField
          label={t('work_preferences.employment_types')}
          ariaLabel={t('work_preferences.employment_types')}
          options={EMPLOYMENT_TYPE_OPTIONS}
          selectedItems={selectedEmploymentTypes}
          onSelectedItemsChange={(items) =>
            setValue('workPreferenceEmploymentTypes', items, {
              shouldDirty: true,
            })
          }
        />

        <MultiSelectField
          label={t('work_preferences.worksites')}
          ariaLabel={t('work_preferences.worksites')}
          options={WORKSITE_OPTIONS}
          selectedItems={selectedWorksites}
          onSelectedItemsChange={(items) =>
            setValue('workPreferenceWorksites', items, { shouldDirty: true })
          }
        />

        <MultiSelectField
          label={t('location.title')}
          ariaLabel={t('location.title')}
          options={[]}
          selectedItems={selectedLocations}
          onSelectedItemsChange={(items) =>
            setValue('workPreferenceLocations', items, { shouldDirty: true })
          }
        />

        <Checkbox
          label={t('work_preferences.willing_to_relocate')}
          value="willingToRelocate"
          checked={!!willingToRelocate}
          onChange={() =>
            setValue('workPreferenceWillingToRelocate', !willingToRelocate, {
              shouldDirty: true,
            })
          }
        />

        <Stack sx={{ alignSelf: 'flex-start' }}>
          <TertiaryButton onClick={clear} size="lg">
            {t('advanced_filters.clear')}
          </TertiaryButton>
        </Stack>
      </Stack>
    </PopoverButton>
  );
};

export default WorkPreferencesFiltersGroup;
